import { useState, useEffect } from 'react';
import { Plus, Trash2, MessageSquare, Star, Eye } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSurveys() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [form, setForm] = useState({ title: '', questions: [''] });

  const getHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchSurveys = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/surveys`, { withCredentials: true, headers: getHeaders() });
      setSurveys(data);
    } catch { setSurveys([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSurveys(); }, []);

  const addQuestion = () => setForm(p => ({ ...p, questions: [...p.questions, ''] }));
  const updateQuestion = (i, val) => setForm(p => ({ ...p, questions: p.questions.map((q, idx) => idx === i ? val : q) }));
  const removeQuestion = (i) => setForm(p => ({ ...p, questions: p.questions.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validQuestions = form.questions.filter(q => q.trim());
    if (!form.title || validQuestions.length === 0) { toast.error('Agrega titulo y al menos una pregunta'); return; }
    try {
      await axios.post(`${API}/admin/surveys`, { title: form.title, questions: validQuestions }, { withCredentials: true, headers: getHeaders() });
      toast.success('Encuesta creada');
      setDialogOpen(false);
      setForm({ title: '', questions: [''] });
      fetchSurveys();
    } catch { toast.error('Error al crear'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar esta encuesta?')) return;
    try {
      await axios.delete(`${API}/admin/surveys/${id}`, { withCredentials: true, headers: getHeaders() });
      toast.success('Eliminada');
      fetchSurveys();
    } catch { toast.error('Error'); }
  };

  return (
    <div data-testid="admin-surveys-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-[#46371c]">Encuestas</h1>
          <p className="font-body text-sm text-[#75664d] mt-1">Crea encuestas de satisfaccion para tus clientes</p>
        </div>
        <Button onClick={() => { setForm({ title: '', questions: [''] }); setDialogOpen(true); }} className="bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="add-survey-btn">
          <Plus className="w-4 h-4 mr-2" /> Nueva Encuesta
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-[#a0d5ba] border-t-transparent rounded-full animate-spin" /></div>
      ) : surveys.length === 0 ? (
        <Card className="p-12 text-center border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl">
          <MessageSquare className="w-12 h-12 mx-auto text-[#e5dfd3] mb-4" />
          <p className="font-body text-[#75664d] mb-4">No hay encuestas creadas</p>
          <Button onClick={() => setDialogOpen(true)} className="bg-[#a0d5ba] text-[#46371c] hover:bg-[#82ba9e] rounded-full font-body">Crear primera encuesta</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {surveys.map(survey => (
            <Card key={survey.id} className="border border-[#e5dfd3] bg-[#fcfbf9] rounded-2xl overflow-hidden" data-testid={`survey-card-${survey.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-heading text-lg font-semibold text-[#46371c]">{survey.title}</h3>
                  <Badge className="bg-[#a0d5ba]/20 text-[#46371c] text-xs">{survey.responses || 0} respuestas</Badge>
                </div>
                <p className="font-body text-xs text-[#75664d] mb-3">{survey.questions?.length || 0} preguntas</p>
                <div className="flex gap-1 pt-3 border-t border-[#e5dfd3]">
                  <button onClick={() => { setSelectedSurvey(survey); }} className="p-2 hover:bg-[#f4f1ea] rounded-lg"><Eye className="w-4 h-4 text-[#75664d]" /></button>
                  <button onClick={() => handleDelete(survey.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg bg-[#fcfbf9]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">Nueva Encuesta</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">Define las preguntas para tus clientes</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="font-body text-sm text-[#75664d] mb-1 block">Titulo *</label>
              <input value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} required className="w-full h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="survey-title-input" placeholder="Ej: Encuesta de satisfaccion Enero 2026" />
            </div>
            <div>
              <label className="font-body text-sm text-[#75664d] mb-2 block">Preguntas</label>
              <div className="space-y-2">
                {form.questions.map((q, i) => (
                  <div key={i} className="flex gap-2">
                    <input value={q} onChange={e => updateQuestion(i, e.target.value)} placeholder={`Pregunta ${i + 1}`} className="flex-1 h-10 px-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid={`question-input-${i}`} />
                    {form.questions.length > 1 && (
                      <button type="button" onClick={() => removeQuestion(i)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
                    )}
                  </div>
                ))}
              </div>
              <Button type="button" variant="outline" onClick={addQuestion} size="sm" className="mt-2 rounded-full border-[#e5dfd3] text-[#75664d] font-body text-xs" data-testid="add-question-btn">
                <Plus className="w-3 h-3 mr-1" /> Agregar pregunta
              </Button>
            </div>
            <div className="flex gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1 rounded-full border-[#e5dfd3] text-[#46371c] font-body">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[#46371c] text-[#fcfbf9] hover:bg-[#2d2312] rounded-full font-body" data-testid="save-survey-btn">Crear Encuesta</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={!!selectedSurvey} onOpenChange={() => setSelectedSurvey(null)}>
        <DialogContent className="max-w-lg bg-[#fcfbf9]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#46371c]">{selectedSurvey?.title}</DialogTitle>
            <DialogDescription className="font-body text-sm text-[#75664d]">{selectedSurvey?.responses || 0} respuestas recibidas</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {selectedSurvey?.questions?.map((q, i) => (
              <div key={i} className="p-3 rounded-xl bg-[#f4f1ea]">
                <p className="font-body text-sm text-[#46371c]"><span className="font-semibold text-[#a0d5ba] mr-2">{i + 1}.</span>{q}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
